import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';

import Layout from '../components/Layout';
import SearchBar from '../components/SearchBar';
import Pagination from '../components/Common/Pagination';
import CustomerInfoModal from '../components/Customer/CustomerInfoModal';
import CreateFollowUpModal from '../components/Followup/CreateFollowupModal';
import LogFollowupModal, { OutcomeType } from '../components/Followup/LogFollowupModal';
import QueryHistoryModal from '../components/Query/QueryHistoryModal';
import AssignmentModal from '../components/Query/AssignmentModal';
import AddBookingModal from '../components/Booking/AddBookingModal';
import VendorQuotationModal from '../components/Query/VendorQuotationModal';
// import ViewQuotationsModal from '../components/Quotation/ViewQuotationsModal';
import CreateQueryModal from '../components/Query/CreateQueryModal';
import CustomerPickerModal from '../components/Customer/CustomerPickerModal';
import { useRole } from '../hooks/usePermissions';
import { toast } from 'react-toastify';
import { isVendorRfqEnabled } from '../utils/vendorRfq';

import { Query } from '../types/Query';
import { Customer } from '../types/Customer';
import { User } from '../types/User';
import { fetchCustomerById } from '../api/Customer';
import { fetchQueries, fetchQueryById, updateQueryStatus } from '../api/Query';
import { fetchFollowups } from '../api/Followup';
import { extractLatestBookingDraft } from '../api/BookingDraft';
import { fetchUsers } from '../api/User';
import { formatDate, formatTime } from '../utils/date';
import { QueryLane, getQueryLane, getQueryStatusValueForLane } from '../utils/queryStatus';
import { fromQueryRooms } from '../components/QuotationBuilder/quotationRoomOccupancy';

import '../css/Pages.css';
import '../css/PackagesPage.css';
import '../css/Kanban.css';
import '../css/modal.css';

import * as FiIcons from 'react-icons/fi';
import QueryCard from '../components/Query/QueryCard';
const FaPlus = FiIcons.FiPlus as unknown as React.FC;

const PAGE_SIZE = 50;

const extractQueryList = (data: any): Query[] => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.queries)) return data.queries;
  if (Array.isArray(data?.items)) return data.items;
  return [];
};

const mergeQueriesById = (existing: Query[], incoming: Query[]) => {
  if (!incoming.length) return existing;
  const existingIds = new Set(existing.map((item) => item.id).filter(Boolean));
  const toAppend = incoming.filter((item) => !item.id || !existingIds.has(item.id));
  return toAppend.length ? [...existing, ...toAppend] : existing;
};

const QueriesPage = () => {
  const role = useRole();
  const vendorRfqEnabled = isVendorRfqEnabled();
  const hasFetched = useRef(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);

  // const [showViewFollowUpsModal, setShowViewFollowUpsModal] = useState(false);
  // const [showViewQuotationsModal, setShowViewQuotationsModal] = useState(false);
  const [showQueryHistory, setShowQueryHistory] = useState(false);
  const [showCreateFollowUpModal, setShowCreateFollowUpModal] = useState(false);
  const [followupRequired, setFollowupRequired] = useState(false);
  const [showLogFollowupModal, setShowLogFollowupModal] = useState(false);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [showCreateBookingModal, setShowCreateBookingModal] = useState(false);
  const [selectedQueryForBooking, setSelectedQueryForBooking] = useState<Query | null>(null);
  const [showQueryModal, setShowQueryModal] = useState(false);
  const [activeQuery, setActiveQuery] = useState<Query | null>(null);
  const [showVendorQuotation, setShowVendorQuotation] = useState(false);
  const [vendorQuotationQuery, setVendorQuotationQuery] = useState<Query | null>(null);

  const [queryId, setQueryId] = useState<number>(0);
  const [followupId, setFollowupId] = useState<number>(0);
  const [queries, setQueries] = useState<Query[]>([]);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreateQueryModal, setShowCreateQueryModal] = useState(false);
  const [showCustomerPicker, setShowCustomerPicker] = useState(false);
  const [newQueryCustomerId, setNewQueryCustomerId] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [serverPaginated, setServerPaginated] = useState(false);
  const [serverTotalPages, setServerTotalPages] = useState(1);
  const [prefetchSeed, setPrefetchSeed] = useState(0);
  const [nextFollowupMap, setNextFollowupMap] = useState<Record<number, string>>({});
  const loadedServerPagesRef = useRef<Set<number>>(new Set());

  const [searchParams, setSearchParams] = useSearchParams();

  const [typeFilter, setTypeFilter] = useState('');
  const [assigneeFilter, setAssigneeFilter] = useState('');
  const navigate = useNavigate();
  const customerIdFromParams = parseInt(searchParams.get('customerId') || '0');

  const load = useCallback(async (customerId?: number) => {
    try {
      setLoading(true);
      const data = await fetchQueries(customerId || 0, { page: 1, pageSize: PAGE_SIZE });
      const list = extractQueryList(data);
      const pagination = (data as any)?.pagination || {};
      const hasPaginationMeta = !Array.isArray(data) && Boolean(
        (data as any)?.pagination ||
        (data as any)?.totalPages ||
        (data as any)?.page ||
        (data as any)?.total ||
        pagination?.totalPages ||
        pagination?.pages,
      );
      const total = Number((data as any)?.total ?? pagination?.total ?? pagination?.count ?? list.length);
      const resolvedTotalPages = Number(
        (data as any)?.totalPages ??
        pagination?.totalPages ??
        pagination?.pages ??
        Math.max(1, Math.ceil(total / PAGE_SIZE)),
      );

      setQueries(list);
      setPage(1);
      if (hasPaginationMeta) {
        setServerPaginated(true);
        setServerTotalPages(Number.isFinite(resolvedTotalPages) && resolvedTotalPages > 0 ? resolvedTotalPages : 1);
        loadedServerPagesRef.current = new Set([1]);
      } else {
        setServerPaginated(false);
        setServerTotalPages(Math.max(1, Math.ceil(list.length / PAGE_SIZE)));
        loadedServerPagesRef.current = new Set();
      }
      setPrefetchSeed((prev) => prev + 1);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      const users = await fetchUsers();
      setAllUsers(Array.isArray(users) ? users : []);
    } catch (err) {
      console.error('Failed to load users:', err);
    }
  }, []);

  const loadNextFollowups = useCallback(async () => {
    try {
      const data = await fetchFollowups();
      const list = Array.isArray(data) ? data : [];
      const now = new Date();
      const nextByQuery = new Map<number, Date>();
      list.forEach((f: any) => {
        const rawDate = String(f?.date || f?.followupDate || '').trim();
        if (!rawDate) return;
        const timeStrRaw: string = String(f?.time || '').trim();
        const timeStr = timeStrRaw ? timeStrRaw.substring(0, 5) : '00:00';
        const datePart = rawDate.includes('T') ? rawDate.split('T')[0] : rawDate.substring(0, 10);
        const composed = `${datePart}T${timeStr}`;
        const d = new Date(composed);
        if (isNaN(d.getTime())) return;
        const qid = Number(f?.queryId || f?.query_id);
        if (!qid) return;
        if (d < now) return;
        const existing = nextByQuery.get(qid);
        if (!existing || d < existing) nextByQuery.set(qid, d);
      });
      const map: Record<number, string> = {};
      nextByQuery.forEach((d, qid) => {
        map[qid] = d.toISOString();
      });
      setNextFollowupMap(map);
    } catch {
      setNextFollowupMap({});
    }
  }, []);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    setPage(1);
    load(customerIdFromParams);
    loadUsers();
    loadNextFollowups();
  }, [customerIdFromParams, load, loadUsers, loadNextFollowups]);

  useEffect(() => {
    if (!serverPaginated || serverTotalPages <= 1) return;

    let cancelled = false;
    const run = async () => {
      for (let nextPage = 2; nextPage <= serverTotalPages; nextPage += 1) {
        if (cancelled) return;
        if (loadedServerPagesRef.current.has(nextPage)) continue;
        try {
          const data = await fetchQueries(customerIdFromParams || 0, { page: nextPage, pageSize: PAGE_SIZE });
          if (cancelled) return;
          const pageList = extractQueryList(data);
          setQueries((prev) => mergeQueriesById(prev, pageList));
          loadedServerPagesRef.current.add(nextPage);
          setPage((prev) => Math.max(prev, nextPage));
        } catch {
          return;
        }
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [prefetchSeed, serverPaginated, serverTotalPages, customerIdFromParams]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, typeFilter, assigneeFilter]);

  const openCreateFollowUpModal = (queryId?: number) => {
    setQueryId(queryId || 0);
    setFollowupRequired(false);
    setShowCreateFollowUpModal(true);
  };

  const openShareQuotationModal = (queryOrId?: Query | number) => {
    const query = typeof queryOrId === 'object' ? queryOrId : queries.find(q => q.id === queryOrId) || activeQuery || null;
    const id = typeof queryOrId === 'number' ? queryOrId : query?.id || 0;
    setQueryId(id);
    void (async () => {
      const latestDraft = id ? await extractLatestBookingDraft(id) : null;
      const providerRef = latestDraft?.providerRef || latestDraft?.provider_ref;

      navigate('/quotation/builder', {
        state: {
          queryId: id,
          hotelRooms: fromQueryRooms(query?.rooms),
          quotationStep: 'builder',
          ...(providerRef && typeof providerRef === 'object'
            ? {
              packageFromDraft: providerRef,
              packageToCopy: providerRef,
            }
            : {}),
        },
      });
    })();
  };

  const viewFollowUps = (queryId?: number) => {
    setQueryId(queryId || 0);
    setShowQueryHistory(true);
  };


  const openCustomerModal = async (customerId?: number) => {
    if (!customerId) return;
    try {
      const customer = await fetchCustomerById(customerId);
      setSelectedCustomer(customer);
    } catch (err) {
    }
  };

  const openAssignmentModal = (queryId?: number) => {
    setQueryId(queryId || 0);
    setShowAssignmentModal(true);
  };

  const openBookingModal = async (targetQueryId: number) => {
    let query = queries.find(q => q.id === targetQueryId) || null;
    if (!query) {
      try {
        query = await fetchQueryById(targetQueryId);
      } catch (error: any) {
        toast.error(error?.message || 'Failed to load query for booking');
        return;
      }
    }
    setSelectedQueryForBooking(query);
    setShowCreateBookingModal(true);
  };

  const openQueryModal = (query: Query) => {
    setActiveQuery(query);
    setShowQueryModal(true);
  };

  const openVendorQuotationModal = async (queryId: number) => {
    if (!vendorRfqEnabled) return;
    let query = queries.find(q => q.id === queryId) || null;
    if (!query && activeQuery?.id === queryId) query = activeQuery;
    if (!query) {
      try {
        query = await fetchQueryById(queryId);
      } catch (error: any) {
        toast.error(error?.message || 'Failed to load query for vendor quotation');
        return;
      }
    }
    setVendorQuotationQuery(query);
    setShowVendorQuotation(true);
  };

  const handleBookingCreated = async (query: Query) => {
    try {
      const updated = await updateQueryStatus(query.id, 'BOOKED');
      setQueries(prev => prev.map(q => (q.id === query.id ? { ...q, status: updated.status } : q)));
      setActiveQuery(prev => (prev?.id === query.id ? { ...prev, status: updated.status } : prev));
      setSelectedQueryForBooking(prev => (prev?.id === query.id ? { ...prev, status: updated.status } : prev));
      setVendorQuotationQuery(prev => (prev?.id === query.id ? { ...prev, status: updated.status } : prev));
      toast.success('Booking created and query marked as booked');
    } catch (error: any) {
      toast.error(error?.message || 'Failed to update query status');
    }
  };

  const handleOutcomeAction = async (outcome: OutcomeType, queryId: number) => {
    if (outcome === 'followup') {
      openCreateFollowUpModal(queryId);
    } else if (outcome === 'quotation') {
      openShareQuotationModal(queryId);
    } else if (outcome === 'booked') {
      await openBookingModal(queryId);
    }
    setFollowupId(0);
  };

  const handleAssign = async (queryId: number, userId: number) => {
    setQueries(prev =>
      prev.map(query =>
        query.id === queryId ? { ...query, assigned_to: userId } : query
      )
    );
  };

  const filteredQueries = useMemo(() => (
    (Array.isArray(queries) ? queries : []).filter(query => {
      const customerName = (query.customer?.name || '').toLowerCase();
      const referenceId = (query.referenceId || '').toLowerCase();
      const matchesSearch = customerName.includes(searchTerm.toLowerCase()) ||
        query.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
        query.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
        referenceId.includes(searchTerm.toLowerCase());

      const matchesType = typeFilter ? query.type === typeFilter : true;
      const matchesAssignee = assigneeFilter ? query.assignedTo?.id?.toString() === assigneeFilter : true;

      return matchesSearch && matchesType && matchesAssignee;
    })
  ), [queries, searchTerm, typeFilter, assigneeFilter]);

  const totalPages = serverPaginated
    ? Math.max(1, serverTotalPages)
    : Math.max(1, Math.ceil(filteredQueries.length / PAGE_SIZE));
  const pagedQueries = useMemo(() => {
    if (serverPaginated) return filteredQueries;
    return filteredQueries.slice(0, page * PAGE_SIZE);
  }, [filteredQueries, page, serverPaginated]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  useEffect(() => {
    if (serverPaginated || loading) return;
    if (page >= totalPages) return;
    const timer = window.setTimeout(() => {
      setPage((prev) => Math.min(totalPages, prev + 1));
    }, 80);
    return () => window.clearTimeout(timer);
  }, [serverPaginated, loading, page, totalPages]);

  const uniqueAssignees = Array.from(
    new Map(
      queries
        .filter(q => q.assignedTo)
        .map(q => [q.assignedTo!.id, q.assignedTo!])
    ).values()
  );

  const newItems = useMemo(
    () => pagedQueries.filter(q => getQueryLane(q.status) === 'NEW'),
    [pagedQueries]
  );
  const quotationSharedItems = useMemo(
    () => pagedQueries.filter(q => getQueryLane(q.status) === 'QUOTATION SHARED'),
    [pagedQueries]
  );
  const closedItems = useMemo(
    () => pagedQueries.filter(q => getQueryLane(q.status) === 'CLOSED'),
    [pagedQueries]
  );
  const bookedItems = useMemo(
    () => pagedQueries.filter(q => getQueryLane(q.status) === 'BOOKED'),
    [pagedQueries]
  );

  const [dragOver, setDragOver] = useState<string | null>(null);
  const [, setDraggingId] = useState<number | null>(null);
  const [lanePositions, setLanePositions] = useState<Record<string, number[]>>({});

  // Initialize and maintain lane order positions without overriding manual reorders
  useEffect(() => {
    setLanePositions(prev => {
      const next: Record<string, number[]> = { ...prev };
      const apply = (status: string, arr: Query[]) => {
        const existing = next[status] || [];
        const ids = arr.map(q => q.id);
        // keep existing order for ids that remain
        const kept = existing.filter(id => ids.includes(id));
        // append new ids in current visible order
        const appended = ids.filter(id => !kept.includes(id));
        next[status] = [...kept, ...appended];
      };
      apply('NEW', newItems);
      apply('QUOTATION SHARED', quotationSharedItems);
      apply('CLOSED', closedItems);
      apply('BOOKED', bookedItems);
      return next;
    });
  }, [newItems, quotationSharedItems, closedItems, bookedItems]);

  const orderLane = (items: Query[], status: string) => {
    const order = lanePositions[status] || [];
    const orderIndex = new Map(order.map((id, idx) => [id, idx] as [number, number]));
    return items.slice().sort((a, b) => {
      const ai = orderIndex.has(a.id) ? (orderIndex.get(a.id) as number) : Number.MAX_SAFE_INTEGER;
      const bi = orderIndex.has(b.id) ? (orderIndex.get(b.id) as number) : Number.MAX_SAFE_INTEGER;
      return ai - bi;
    });
  };

  const newList = orderLane(newItems, 'NEW');
  const quotationSharedList = orderLane(quotationSharedItems, 'QUOTATION SHARED');
  const closedList = orderLane(closedItems, 'CLOSED');
  const bookedList = orderLane(bookedItems, 'BOOKED');

  const applyDropChange = async (status: QueryLane, id: number, beforeId?: number | null) => {
    setLanePositions(prev => {
      const next = { ...prev } as Record<string, number[]>;
      Object.keys(next).forEach(k => {
        next[k] = (next[k] || []).filter(x => x !== id);
      });
      const lane = status;
      const arr = next[lane] ? [...next[lane]] : [];
      let insertAt = 0;
      if (beforeId && arr.includes(beforeId)) {
        insertAt = arr.indexOf(beforeId);
      }
      arr.splice(insertAt, 0, id);
      next[lane] = arr;
      return next;
    });

    const nextStatus = getQueryStatusValueForLane(status);
    setQueries(prev => prev.map(q => (q.id === id ? { ...q, status: nextStatus } : q)));
    setActiveQuery(prev => (prev?.id === id ? { ...prev, status: nextStatus } : prev));
    setSelectedQueryForBooking(prev => (prev?.id === id ? { ...prev, status: nextStatus } : prev));
    setVendorQuotationQuery(prev => (prev?.id === id ? { ...prev, status: nextStatus } : prev));
    try {
      await updateQueryStatus(id, status);
    } catch (e) {
      const customerId = parseInt(searchParams.get('customerId') || '0');
      load(customerId || undefined);
    }
  };

  const handleDropTo = async (status: QueryLane, ev: React.DragEvent<HTMLElement>, beforeId?: number | null) => {
    ev.preventDefault();
    ev.stopPropagation(); // Prevent event from bubbling to parent handlers
    setDragOver(null);
    const idStr = ev.dataTransfer.getData('text/plain');
    const id = parseInt(idStr || '0');
    if (!id) return;

    // Find the query to check its current status
    const query = queries.find(q => q.id === id);
    const currentStatus = query ? getQueryLane(query.status || '') : 'NEW';
    
    // Prevent moving from BOOKED to other statuses
    if (currentStatus === 'BOOKED' && status !== 'BOOKED') {
      toast.error('Cannot move a booked query to another status');
      return;
    }
    
    const isMovingToBooked = status === 'BOOKED';
    const isStatusChange = currentStatus !== status;
    if (isMovingToBooked && isStatusChange) {
      await openBookingModal(id);
      return;
    }

    await applyDropChange(status, id, beforeId);
  };

  const getAssigneeName = (query: Query) =>
    query.assignedTo?.name ||
    allUsers.find(user => user.id === query.assigned_to)?.name ||
    'Unassigned';

  const getNextFollowupLabel = (queryId: number) => {
    const iso = nextFollowupMap[queryId];
    if (!iso) return '';
    const date = formatDate(iso);
    const time = formatTime(iso);
    return [date, time].filter(Boolean).join(' ');
  };

  const getAssigneeCounts = (items: Query[]) => {
    const counts = items.reduce<Record<string, number>>((acc, item) => {
      const name = getAssigneeName(item);
      acc[name] = (acc[name] || 0) + 1;
      return acc;
    }, {});
    return Object.entries(counts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  };

  const renderAssigneeCounts = (items: Query[]) => {
    const counts = getAssigneeCounts(items);
    if (counts.length === 0) return null;
    return (
      <div className="lane-assignees">
        {counts.map(({ name, count }) => (
          <span key={name} className="lane-assignee-chip">
            {name}: {count}
          </span>
        ))}
      </div>
    );
  };

  const handleCardDragStart = (queryId: number, e: React.DragEvent<HTMLDivElement>) => {
    e.dataTransfer.setData('text/plain', String(queryId));
    setDraggingId(queryId);
  };

  const handleCardDragOver = (lane: QueryLane, e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(lane);
  };

  const handleCardDrop = (lane: QueryLane, e: React.DragEvent<HTMLDivElement>, beforeId?: number | null) => {
    handleDropTo(lane, e, beforeId);
  };

  return (
    <Layout>
      <div className="crm-page packages-page">
        <div className="packages-header">
          <div>
            <h2>Customer Queries</h2>
            <p className="page-hint">Organize active leads through the CRM lanes without changing the current workflow structure.</p>
          </div>
          <div className="packages-actions">
            <button
              className="btn"
              onClick={() => {
                if (customerIdFromParams) {
                  setNewQueryCustomerId(customerIdFromParams);
                  setShowCreateQueryModal(true);
                } else {
                  setShowCustomerPicker(true);
                }
              }}
              title={customerIdFromParams ? 'Create new query for selected customer' : 'Pick a customer to create a query'}
            >
              <FaPlus /> Add Query
            </button>
          </div>
        </div>

        <div className="packages-filters">
          <SearchBar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            placeholder="Search by name, location, type, reference ID..."
          />
          <select onChange={(e) => setTypeFilter(e.target.value)} value={typeFilter} className="filter-select" aria-label="Type">
            <option value="">All Types</option>
            <option value="travel">Travel</option>
            <option value="event">Event</option>
          </select>
          <select onChange={(e) => setAssigneeFilter(e.target.value)} value={assigneeFilter} className="filter-select" aria-label="Assignee">
            <option value="">All Assignees</option>
            {uniqueAssignees.map(user => (
              <option key={user.id} value={user.id}>{user.name}</option>
            ))}
          </select>
          <button className="clear-btn" onClick={() => {
            setSearchTerm('');
            setTypeFilter('');
            setAssigneeFilter('');
            setSearchParams({});
            load();
          }}>Clear</button>
        </div>

        <div className="kanban-board">
        <div
          className={`kanban-column open ${dragOver === 'NEW' ? 'drop-target' : ''}`}
          onDragOver={(e) => e.preventDefault()}
          onDragEnter={() => setDragOver('NEW')}
          onDragLeave={() => setDragOver(null)}
          onDrop={(e) => handleDropTo('NEW', e)}
        >
          <div className="kanban-column-header">
            New ({newItems.length})
            {renderAssigneeCounts(newItems)}
          </div>
          {newList.map(q => (
            <QueryCard
              key={q.id}
              query={q}
              lane="NEW"
              assigneeName={getAssigneeName(q)}
              role={role}
              onOpen={openQueryModal}
              nextFollowupLabel={getNextFollowupLabel(q.id)}
              formatDate={formatDate}
              onOpenCustomer={openCustomerModal}
              onAddFollowup={openCreateFollowUpModal}
              onShareQuotation={openShareQuotationModal}
              onViewHistory={viewFollowUps}
              onAssign={openAssignmentModal}
              onVendorRfq={vendorRfqEnabled ? openVendorQuotationModal : undefined}
              onDragStart={handleCardDragStart}
              onDragOver={handleCardDragOver}
              onDrop={handleCardDrop}
            />
          ))}
        </div>
        <div
          className={`kanban-column inprogress ${dragOver === 'QUOTATION SHARED' ? 'drop-target' : ''}`}
          onDragOver={(e) => e.preventDefault()}
          onDragEnter={() => setDragOver('QUOTATION SHARED')}
          onDragLeave={() => setDragOver(null)}
          onDrop={(e) => handleDropTo('QUOTATION SHARED', e)}
        >
          <div className="kanban-column-header">
            Quotation shared ({quotationSharedItems.length})
            {renderAssigneeCounts(quotationSharedItems)}
          </div>
          {quotationSharedList.map(q => (
            <QueryCard
              key={q.id}
              query={q}
              lane="QUOTATION SHARED"
              assigneeName={getAssigneeName(q)}
              role={role}
              onOpen={openQueryModal}
              nextFollowupLabel={getNextFollowupLabel(q.id)}
              formatDate={formatDate}
              onOpenCustomer={openCustomerModal}
              onAddFollowup={openCreateFollowUpModal}
              onShareQuotation={openShareQuotationModal}
              onViewHistory={viewFollowUps}
              onAssign={openAssignmentModal}
              onVendorRfq={vendorRfqEnabled ? openVendorQuotationModal : undefined}
              onDragStart={handleCardDragStart}
              onDragOver={handleCardDragOver}
              onDrop={handleCardDrop}
            />
          ))}
        </div>
        <div
          className={`kanban-column booked ${dragOver === 'BOOKED' ? 'drop-target' : ''}`}
          onDragOver={(e) => e.preventDefault()}
          onDragEnter={() => setDragOver('BOOKED')}
          onDragLeave={() => setDragOver(null)}
          onDrop={(e) => handleDropTo('BOOKED', e)}
        >
          <div className="kanban-column-header">
            Booked ({bookedItems.length})
            {renderAssigneeCounts(bookedItems)}
          </div>
          {bookedList.map(q => (
            <QueryCard
              key={q.id}
              query={q}
              lane="BOOKED"
              assigneeName={getAssigneeName(q)}
              role={role}
              onOpen={openQueryModal}
              nextFollowupLabel={getNextFollowupLabel(q.id)}
              formatDate={formatDate}
              onOpenCustomer={openCustomerModal}
              onAddFollowup={openCreateFollowUpModal}
              onShareQuotation={openShareQuotationModal}
              onViewHistory={viewFollowUps}
              onAssign={openAssignmentModal}
              onVendorRfq={vendorRfqEnabled ? openVendorQuotationModal : undefined}
              onDragStart={handleCardDragStart}
              onDragOver={handleCardDragOver}
              onDrop={handleCardDrop}
            />
          ))}
        </div>
        <div
          className={`kanban-column closed ${dragOver === 'CLOSED' ? 'drop-target' : ''}`}
          onDragOver={(e) => e.preventDefault()}
          onDragEnter={() => setDragOver('CLOSED')}
          onDragLeave={() => setDragOver(null)}
          onDrop={(e) => handleDropTo('CLOSED', e)}
        >
          <div className="kanban-column-header">
            Closed ({closedItems.length})
            {renderAssigneeCounts(closedItems)}
          </div>
          {closedList.map(q => (
            <QueryCard
              key={q.id}
              query={q}
              lane="CLOSED"
              assigneeName={getAssigneeName(q)}
              role={role}
              onOpen={openQueryModal}
              nextFollowupLabel={getNextFollowupLabel(q.id)}
              formatDate={formatDate}
              onOpenCustomer={openCustomerModal}
              onAddFollowup={openCreateFollowUpModal}
              onShareQuotation={openShareQuotationModal}
              onViewHistory={viewFollowUps}
              onAssign={openAssignmentModal}
              onVendorRfq={vendorRfqEnabled ? openVendorQuotationModal : undefined}
              onDragStart={handleCardDragStart}
              onDragOver={handleCardDragOver}
              onDrop={handleCardDrop}
            />
          ))}
        </div>
        </div>
        <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
      </div>

      {selectedCustomer && (
        <CustomerInfoModal
          customer={selectedCustomer}
          onClose={() => setSelectedCustomer(null)}
        />
      )}

      {showCreateFollowUpModal && (
        <CreateFollowUpModal
          queryId={queryId}
          required={followupRequired}
          onCreated={async () => {
            loadNextFollowups();
          }}
          backdropClassName="modal-level-2"
          onClose={() => {
            setShowCreateFollowUpModal(false);
            setFollowupRequired(false);
          }}
        />
      )}

      {showCreateQueryModal && newQueryCustomerId && (
        <CreateQueryModal
          customerId={newQueryCustomerId}
          onClose={() => setShowCreateQueryModal(false)}
          onQueryCreated={(newId) => {
            setShowCreateQueryModal(false);
            const refId = customerIdFromParams || newQueryCustomerId || 0;
            setNewQueryCustomerId(null);
            load(refId || undefined);
            setQueryId(newId);
            setFollowupRequired(true);
            setShowCreateFollowUpModal(true);
          }}
        />
      )}

      {showCustomerPicker && (
        <CustomerPickerModal
          onClose={() => setShowCustomerPicker(false)}
          onSelect={(c) => {
            setShowCustomerPicker(false);
            setNewQueryCustomerId(c.id!);
            setShowCreateQueryModal(true);
          }}
        />
      )}

      {showQueryHistory && (
        <QueryHistoryModal
          queryId={queryId}
          backdropClassName="modal-level-2"
          onClose={() => setShowQueryHistory(false)}
          onLog={(followupId) => {
            setShowQueryHistory(false);
            setFollowupId(followupId);
            setShowLogFollowupModal(true);
          }}
        />
      )}

      {showLogFollowupModal && (
        <LogFollowupModal
          queryId={queryId}
          followupId={followupId}
          onClose={() => {
            setShowLogFollowupModal(false);
            setFollowupId(0);
          }}
          onOutcomeAction={handleOutcomeAction}
        />
      )}

      {showAssignmentModal && (
        <AssignmentModal
          queryId={queryId}
          users={allUsers}
          backdropClassName="modal-level-2"
          onClose={() => setShowAssignmentModal(false)}
          onAssign={handleAssign}
        />
      )}

      {showCreateBookingModal && selectedQueryForBooking && (
        <AddBookingModal
          query={selectedQueryForBooking}
          onClose={() => {
            setShowCreateBookingModal(false);
            setSelectedQueryForBooking(null);
          }}
          onCreated={() => handleBookingCreated(selectedQueryForBooking)}
        />
      )}

      {showQueryModal && activeQuery && (
        <div className="modal-backdrop modal-level-1" onClick={() => setShowQueryModal(false)}>
          <div className="modal modal-lg query-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-header-actions">
                {vendorRfqEnabled && (
                  <button
                    className="btn"
                    type="button"
                    onClick={() => openVendorQuotationModal(activeQuery.id)}
                  >
                    + Vendor Quotation
                  </button>
                )}
              </div>
              <button className="close-btn" onClick={() => setShowQueryModal(false)}>&times;</button>
            </div>
            <div className="query-modal-content">
              <QueryCard
                query={activeQuery}
                lane={getQueryLane(activeQuery.status || '')}
                assigneeName={getAssigneeName(activeQuery)}
                role={role}
                variant="modal"
                nextFollowupLabel={getNextFollowupLabel(activeQuery.id)}
                formatDate={formatDate}
                onOpenCustomer={openCustomerModal}
                onAddFollowup={openCreateFollowUpModal}
                onShareQuotation={openShareQuotationModal}
                onViewHistory={viewFollowUps}
                onAssign={openAssignmentModal}
                onVendorRfq={vendorRfqEnabled ? openVendorQuotationModal : undefined}
              />
            </div>
          </div>
        </div>
      )}

      {showVendorQuotation && vendorQuotationQuery && (
        <VendorQuotationModal
          isOpen={showVendorQuotation}
          query={vendorQuotationQuery}
          backdropClassName="modal-level-2"
          onClose={() => {
            setShowVendorQuotation(false);
            setVendorQuotationQuery(null);
          }}
        />
      )}
    </Layout>
  );
};

export default QueriesPage;
